import React, { useState } from 'react';
import moment from 'moment';
import get from 'lodash/get';
import { useQuery } from '@apollo/react-hooks';
import { Button, Input, Space, Table, Tabs, Tag } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import NumberFormat from 'react-number-format';

import { GET_INVESTMENTS, INVESTMENT_COUNT } from './apollo_queries';
import { NoteImage } from '../components/NoteImage';
import { NOTE_TYPES } from '../components/note-form/fields';

// One tab per PRODUCT, as the investor dashboard sells them (TL, 2026-09-18): Individual Notes (backed
// by a property, listed by hand — Add Property), Credit Fund, and Constitution Notes (the Constitution
// and Rolling notes the note-cycle scheduler mints, plus the Demand note the platform keeps open). The
// system products are never created here. The tab and the page live in the URL (`?tab=&p=`); the search
// box is shared. `deleted` is the soft-delete the backend's adminDeleteInvestment writes (the panel does not
// offer it); the note stays listed with its red tag.
const STATUS_TAG = {
  available_investment: { color: 'green', label: 'Available Investment' },
  previously_funded: { color: 'default', label: 'Previously Funded' },
  deleted: { color: 'red', label: 'Deleted' },
};

const TAB_INDIVIDUAL = 'individual';
const TAB_CREDIT_FUND = 'credit-fund';
const TAB_CONSTITUTION = 'constitution';
// Which stored product types each system tab shows.
const TAB_TYPES = {
  [TAB_CREDIT_FUND]: [NOTE_TYPES.creditFund],
  [TAB_CONSTITUTION]: [
    NOTE_TYPES.constitution,
    NOTE_TYPES.rolling,
    NOTE_TYPES.demand,
  ],
};

// The product tag of a system note. A stored value outside this table shows as-is.
const PRODUCT_TAG = {
  [NOTE_TYPES.rolling]: { color: 'purple', label: 'Rolling' },
  [NOTE_TYPES.constitution]: { color: 'geekblue', label: 'Constitution' },
  [NOTE_TYPES.creditFund]: { color: 'gold', label: 'Credit Fund' },
  [NOTE_TYPES.demand]: { color: 'cyan', label: 'Demand' },
};

// `getInvestments.noteType` is ONE exact value (no "any of these"), so the system tabs fetch the
// catalogue in a single page and keep their product types on the client. The catalogue is
// ~100 notes in prod and the system mints a handful a year; the page size is the safety margin.
const SYSTEM_FETCH_COUNT = 500;
const PAGE_SIZE = 10;

const { Search } = Input;
const { TabPane } = Tabs;

// antd sorter column field -> getInvestments `InvestmentSortField` enum value name.
const SORT_FIELD = {
  address: 'Address',
  maturityDate: 'MaturityDate',
};

const money = (n) => (
  <NumberFormat
    displayType="text"
    value={n}
    thousandSeparator={true}
    prefix={'$'}
    renderText={(value) => <div>{value}</div>}
  />
);

// altoRegistered = the note already has its Alto offering (its listing # is locked in the detail).
const statusCell = (status, { altoRegistered }) => {
  const tag = STATUS_TAG[status] || { color: 'default', label: status };
  return (
    <span>
      {status ? <Tag color={tag.color}>{tag.label}</Tag> : 'N/A'}
      {altoRegistered && <Tag color="blue">Alto</Tag>}
    </span>
  );
};

const actionCell = (id) => (
  <Space size="middle">
    <Link style={{ color: '#1abc9c' }} to={`/properties-and-investments/${id}`}>
      Update
    </Link>
  </Space>
);

const maturityColumn = {
  title: 'Maturity date',
  dataIndex: 'maturityDate',
  sorter: true,
  showSorterTooltip: false,
  render: (d) => (d ? <span>{moment(d).format('LLL')}</span> : 'N/A'),
};

const PROPERTY_COLUMNS = [
  {
    title: 'Image',
    dataIndex: 'imageUrl',
    render: (url) => (
      <NoteImage src={url} alt="lending" width={40} height={40} />
    ),
  },
  {
    title: 'Address',
    dataIndex: 'address',
    sorter: true,
    showSorterTooltip: false,
  },
  maturityColumn,
  { title: 'Availability', dataIndex: 'status', render: statusCell },
  { title: 'Funded', dataIndex: 'investmentBalance', render: money },
  { title: 'Action', dataIndex: '_id', render: actionCell },
];

const INVESTMENT_COLUMNS = [
  {
    title: 'Product',
    dataIndex: 'noteType',
    render: (noteType) => {
      const tag = PRODUCT_TAG[noteType] || {
        color: 'default',
        label: noteType,
      };
      return <Tag color={tag.color}>{tag.label}</Tag>;
    },
  },
  {
    title: 'Name',
    dataIndex: 'address',
    sorter: true,
    showSorterTooltip: false,
  },
  {
    title: 'Listing',
    dataIndex: 'noteListing',
    render: (listing) => (listing ? <code>{listing}</code> : '—'),
  },
  maturityColumn,
  { title: 'Availability', dataIndex: 'status', render: statusCell },
  { title: 'Funded', dataIndex: 'investmentBalance', render: money },
  { title: 'Action', dataIndex: '_id', render: actionCell },
];

const pagination = (total, page) => ({
  total,
  pageSize: PAGE_SIZE,
  position: ['bottomCenter'],
  current: page,
  showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
  hideOnSinglePage: true,
  showSizeChanger: false,
});

// Sorting is server-side on both tabs; the sorter change is read the same way.
function useSorter() {
  const [sortBy, setSortBy] = useState('UpdatedAt');
  const [sortDirection, setSortDirection] = useState('DESC');

  const onSort = (sorter) => {
    if (!sorter || !sorter.field) return;
    const mapped = SORT_FIELD[sorter.field] || 'UpdatedAt';
    if (mapped !== sortBy) setSortBy(mapped);
    if (sorter.order)
      setSortDirection(sorter.order === 'ascend' ? 'ASC' : 'DESC');
  };

  return { sortBy, sortDirection, onSort };
}

// Individual notes only, paginated by the backend (list + count share the filter).
function PropertiesTab({ q, page, onPage }) {
  const { sortBy, sortDirection, onSort } = useSorter();
  const filters = { q, noteType: NOTE_TYPES.individual };
  const countQuery = useQuery(INVESTMENT_COUNT, { variables: filters });
  const total = get(countQuery, 'data.investmentCount', 0);
  const { data, loading } = useQuery(GET_INVESTMENTS, {
    variables: { ...filters, count: PAGE_SIZE, page, sortBy, sortDirection },
  });
  const rows = get(data, 'getInvestments', []);
  const busy = loading || countQuery.loading;

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Link to="/add-property">
          <Button type="primary" icon={<PlusOutlined />} size="large">
            Add Property
          </Button>
        </Link>
      </div>
      {busy && (
        <div className="web-load-wrap">
          <LoadingOutlined />
        </div>
      )}
      <Table
        rowKey={'_id'}
        columns={PROPERTY_COLUMNS}
        dataSource={rows}
        onChange={(p, _, sorter) => {
          if (p.current !== page) onPage(p.current);
          onSort(sorter);
        }}
        pagination={pagination(total, page)}
        loading={busy}
      />
    </>
  );
}

// System products only. One fetch, filtered by the tab's product types and paginated on the client
// (see SYSTEM_FETCH_COUNT).
function InvestmentsTab({ q, page, onPage, types, blurb }) {
  const { sortBy, sortDirection, onSort } = useSorter();
  const { data, loading } = useQuery(GET_INVESTMENTS, {
    variables: { q, count: SYSTEM_FETCH_COUNT, page: 1, sortBy, sortDirection },
  });
  const rows = get(data, 'getInvestments', []).filter((note) =>
    types.includes(note.noteType)
  );

  return (
    <>
      <div style={{ marginBottom: 16, color: '#8c8c8c' }}>{blurb}</div>
      {loading && (
        <div className="web-load-wrap">
          <LoadingOutlined />
        </div>
      )}
      <Table
        rowKey={'_id'}
        columns={INVESTMENT_COLUMNS}
        dataSource={rows}
        onChange={(p, _, sorter) => {
          if (p.current !== page) onPage(p.current);
          onSort(sorter);
        }}
        pagination={pagination(rows.length, page)}
        loading={loading}
      />
    </>
  );
}

export function Properties() {
  const history = useHistory();
  const location = useLocation();
  const [query, setQuery] = useState('');
  // The Search button re-runs the queries with the typed text; typing alone does not.
  const [q, setQ] = useState('');

  const { p, tab: tabParam } = qs.parse(location.search);
  const tab = TAB_TYPES[tabParam] ? tabParam : TAB_INDIVIDUAL;
  const page = parseInt(p, 10) || 1;

  const navigate = (next) =>
    history.push(`?${qs.stringify({ tab, p: page, ...next })}`);
  const onPage = (next) => navigate({ p: next });

  return (
    <div style={{ padding: 15, paddingLeft: 10, paddingRight: 10 }}>
      <Search
        placeholder="Search here ..."
        enterButton="Search"
        size="large"
        onSearch={(value) => {
          setQ(value);
          navigate({ p: 1 });
        }}
        onChange={(e) => setQuery(e.target.value)}
        value={query}
      />
      <Tabs
        activeKey={tab}
        // A page number belongs to one tab: switching starts the other at page 1 (the search stays).
        onChange={(key) => navigate({ tab: key, p: 1 })}
        size="large"
      >
        <TabPane tab="Individual Notes" key={TAB_INDIVIDUAL}>
          {tab === TAB_INDIVIDUAL && (
            <PropertiesTab q={q} page={page} onPage={onPage} />
          )}
        </TabPane>
        <TabPane tab="Credit Fund" key={TAB_CREDIT_FUND}>
          {tab === TAB_CREDIT_FUND && (
            <InvestmentsTab
              q={q}
              page={page}
              onPage={onPage}
              types={TAB_TYPES[TAB_CREDIT_FUND]}
              blurb="A single fund the platform keeps open — created by the initializer, never here."
            />
          )}
        </TabPane>
        <TabPane tab="Constitution Notes" key={TAB_CONSTITUTION}>
          {tab === TAB_CONSTITUTION && (
            <InvestmentsTab
              q={q}
              page={page}
              onPage={onPage}
              types={TAB_TYPES[TAB_CONSTITUTION]}
              blurb="Constitution and Rolling notes minted by the note-cycle scheduler, plus the Demand note — never created here."
            />
          )}
        </TabPane>
      </Tabs>
    </div>
  );
}
